import React from 'react'
import { Tooltip } from '@mui/material';
import {
    Announcements,
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    ScreenReaderInstructions,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { FileResponse, FileSource } from '../interfaces/FileResponse';
import { MovementType } from '../search/movement-button.component';
import { isPdfItem } from '../search/use-sheet-source';
import { ChevronDownIcon, ChevronsDownIcon, ChevronsUpIcon, ChevronUpIcon, FileTextIcon, GripIcon, IconProps, ImageIcon, TrashIcon } from '../icons/icons';
import styles from './repertoire-list.module.css'

export interface RepertoireListProps {
    items: FileResponse[],
    // A move is in flight; buttons and dragging wait so the order on screen matches the server
    busy?: boolean,
    onMove: (file: FileResponse, type: MovementType) => void,
    // Drag and drop: move the sheet from one position to another
    onReorder: (file: FileResponse, from: number, to: number) => void,
    onOpen: (index: number) => void,
}

const ACTIONS: { type: MovementType, Icon: React.ComponentType<IconProps>, tip: string }[] = [
    { type: MovementType.First, Icon: ChevronsUpIcon, tip: 'Mover para o início' },
    { type: MovementType.Up, Icon: ChevronUpIcon, tip: 'Subir uma posição' },
    { type: MovementType.Down, Icon: ChevronDownIcon, tip: 'Descer uma posição' },
    { type: MovementType.Last, Icon: ChevronsDownIcon, tip: 'Mover para o final' },
];

// Readable name: collection files are named after the song; crawler links end with the file name
export const sheetName = (item: FileResponse) => {
    const raw = item.source === FileSource.Dropbox
        ? item.address
        : item.address.split('?')[0].split('/').filter(Boolean).pop() || item.address;

    let name = raw;
    try {
        name = decodeURIComponent(raw);
    } catch { }

    return name.replace(/\.[a-z0-9]{2,4}$/i, '').replace(/[_+]/g, ' ').trim() || item.address;
}

const sheetId = (item: FileResponse) => item.serverPath || item.address;

const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: 'Para mover, pressione espaço ou enter, use as setas para escolher a nova posição e pressione espaço ou enter de novo. Esc cancela.',
};

export default function RepertoireList({ items, busy, onMove, onReorder, onOpen }: RepertoireListProps) {
    const sensors = useSensors(
        // A small move threshold keeps a plain tap on the handle from starting a drag
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const ids = items.map(sheetId);
    const positionOf = (id: string | number) => ids.indexOf(String(id)) + 1;
    const nameOf = (id: string | number) => {
        const item = items.find(i => sheetId(i) === String(id));
        return item ? sheetName(item) : '';
    };

    const announcements: Announcements = {
        onDragStart: ({ active }) => `${nameOf(active.id)} selecionada, posição ${positionOf(active.id)} de ${items.length}.`,
        onDragOver: ({ active, over }) => over ? `${nameOf(active.id)} na posição ${positionOf(over.id)} de ${items.length}.` : '',
        onDragEnd: ({ active, over }) => over ? `${nameOf(active.id)} movida para a posição ${positionOf(over.id)}.` : '',
        onDragCancel: ({ active }) => `Movimento cancelado. ${nameOf(active.id)} continua na posição ${positionOf(active.id)}.`,
    };

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) return;

        const from = ids.indexOf(String(active.id));
        const to = ids.indexOf(String(over.id));

        if (from >= 0 && to >= 0)
            onReorder(items[from], from, to);
    };

    return <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={onDragEnd}
        accessibility={{ announcements, screenReaderInstructions }}
    >
        <SortableContext items={ids} strategy={verticalListSortingStrategy} disabled={busy}>
            <ol className={styles.list}>
                {items.map((item, index) => <SheetRow
                    key={sheetId(item)}
                    item={item}
                    index={index}
                    count={items.length}
                    busy={busy}
                    onMove={onMove}
                    onOpen={onOpen}
                />)}
            </ol>
        </SortableContext>
    </DndContext>
}

interface SheetRowProps {
    item: FileResponse,
    index: number,
    count: number,
    busy?: boolean,
    onMove: (file: FileResponse, type: MovementType) => void,
    onOpen: (index: number) => void,
}

function SheetRow({ item, index, count, busy, onMove, onOpen }: SheetRowProps) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: sheetId(item) });

    const isPdf = isPdfItem(item);
    const name = sheetName(item);
    const isFirst = index === 0;
    const isLast = index === count - 1;

    const disabled = (type: MovementType) => busy
        || ((type === MovementType.First || type === MovementType.Up) && isFirst)
        || ((type === MovementType.Down || type === MovementType.Last) && isLast);

    return <li
        ref={setNodeRef}
        className={isDragging ? `${styles.row} ${styles.dragging}` : styles.row}
        style={{ transform: CSS.Transform.toString(transform), transition }}
    >
        <button
            type='button'
            ref={setActivatorNodeRef}
            className={styles.handle}
            disabled={busy}
            {...attributes}
            {...listeners}
            aria-label={`Arrastar para mover: ${name}`}
            title='Arraste para mover'
        >
            <GripIcon size={18} />
        </button>

        <span className={styles.position}>{index + 1}</span>

        <Tooltip title={isPdf ? 'Arquivo PDF' : 'Imagem'} arrow>
            <span className={isPdf ? `${styles.type} ${styles.pdf}` : styles.type} role='img' aria-label={isPdf ? 'PDF' : 'Imagem'}>
                {isPdf ? <FileTextIcon size={20} /> : <ImageIcon size={20} />}
            </span>
        </Tooltip>

        <button type='button' className={styles.name} onClick={() => onOpen(index)} title={`Ver ${name}`}>
            {name}
        </button>

        <div className={styles.actions}>
            {ACTIONS.map(({ type, Icon, tip }) => <Tooltip key={type} title={tip} arrow>
                <span>
                    <button
                        type='button'
                        className={styles.action}
                        aria-label={`${tip}: ${name}`}
                        disabled={disabled(type)}
                        onClick={() => onMove(item, type)}
                    >
                        <Icon size={18} />
                    </button>
                </span>
            </Tooltip>)}

            <Tooltip title='Excluir do repertório' arrow>
                <span>
                    <button
                        type='button'
                        className={`${styles.action} ${styles.remove}`}
                        aria-label={`Excluir do repertório: ${name}`}
                        disabled={busy}
                        onClick={() => onMove(item, MovementType.Remove)}
                    >
                        <TrashIcon size={18} />
                    </button>
                </span>
            </Tooltip>
        </div>
    </li>
}
